import { Anchor } from 'antd';
import style from './style.module.scss';
const { Link } = Anchor;
import { forwardRef, useImperativeHandle, useState } from 'react';

class AnchorNode {
  id: string;
  title: string;
  level: number;
  children: AnchorNode[];
  constructor(heading: Element, indexes: number[] = []) {
    this.title = heading.textContent.trim();
    this.id = `${indexes.join('.')}_${this.title}`;
    this.level = Number(heading.nodeName[1]);
    this.children = [];
    heading.id = this.id;
  }
}

const hangTheHeadingOnTheTree = (
  root: AnchorNode,
  heading: Element,
  path: number[] = []
) => {
  if (root.level < Number(heading.nodeName[1]) - 1) {
    const next = root.children[root.children.length - 1];
    // If there is no one could serve as parent, construct a empty node, build a bridge
    if (!next) {
      const emptyNode = new AnchorNode(
        document.createElement(`h${root.level + 1}`)
      );
      root.children.push(emptyNode);
      return hangTheHeadingOnTheTree(emptyNode, heading, path);
    }
    path.push(root.children.length);
    return hangTheHeadingOnTheTree(next, heading, path);
  }
  path.push(root.children.length + 1);
  // Create a new node for the heading
  const node = new AnchorNode(heading, path);
  // Add the heading as a child of the parent node
  root.children.push(node);
};

const LinkTree = ({ items }: { items: AnchorNode[] }): JSX.Element => {
  return (
    <>
      {items.map((item) =>
        item.title === '' ? (
          <div key={item.id} className="ant-anchor-link">
            <LinkTree items={item.children} />
          </div>
        ) : (
          <Link href={`#${item.id}`} title={item.title} key={item.id}>
            {item.children.length > 0 && <LinkTree items={item.children} />}
          </Link>
        )
      )}
    </>
  );
};

const cutRedundantRoot = (nodes: AnchorNode[]): AnchorNode[] => {
  if (nodes.length === 1 && nodes[0].title === '') {
    return cutRedundantRoot(nodes[0].children);
  }
  return nodes;
};

const getAnchorNodeList = (scope = '#markdown', limit = 5): AnchorNode[] => {
  const targetTags = Array.from({ length: limit }, (_, i) => `h${i + 1}`).join(
    ','
  );
  // Find all the heading tags (1 to limit)
  const headings = document.querySelectorAll(`${scope} ${targetTags}`);
  // Create a virtual AnchorNode as root
  const vRoot: AnchorNode = new AnchorNode(document.createElement('h0'));
  // Loop through the NodeList and build the tree structure
  headings.forEach((heading) => hangTheHeadingOnTheTree(vRoot, heading));
  // Trim the tree so that the final result tree has the most level node list
  return cutRedundantRoot(vRoot.children);
};

export interface AnchorRef {
  display: () => void;
}

interface AnchorProps {
  scope: string;
  limit: number;
}

const ContentAnchor: React.ForwardRefExoticComponent<
  AnchorProps & React.RefAttributes<AnchorRef>
> = forwardRef<AnchorRef, AnchorProps>((props, ref) => {
  const { scope, limit } = props;
  const [anchorItems, setAnchorItems] = useState<AnchorNode[]>([]);

  useImperativeHandle(
    ref,
    () => {
      return {
        display: () => {
          const list = getAnchorNodeList(`#${scope}`, limit);
          setAnchorItems(list);
        },
      };
    },
    []
  );

  const handleAnchorChange = (v: string) => {
    const element = document.querySelector(
      `.ant-anchor [href="${v}"]`
    ) as HTMLElement;
    if (!element) return;
    const container = document.querySelector('.ant-anchor');

    const buffer = 12;

    element.offsetTop + element.clientHeight + buffer >
      container.clientHeight &&
      container.scrollTo({
        top:
          element.offsetTop +
          element.clientHeight +
          buffer -
          container.clientHeight,
        behavior: 'smooth',
      });

    element.offsetTop - buffer < 0 &&
      container.scrollTo({
        top: element.offsetTop - buffer,
        behavior: 'smooth',
      });
  };
  return (
    <Anchor
      affix={false}
      showInkInFixed={true}
      className={style['anchor']}
      offsetTop={60}
      onChange={handleAnchorChange}
    >
      <LinkTree items={anchorItems}></LinkTree>
    </Anchor>
  );
});

ContentAnchor.displayName = 'ContentAnchor';
export default ContentAnchor;
